import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';
import { SeedDiscoveryPanel } from '../components/SeedDiscoveryPanel';

interface SessionDetail {
  id: string;
  session_num: number;
  phase: string;
  scheduled_at: string;
  status: string;
  energy_score: number | null;
  trainer_notes: string | null;
}

interface EnrolmentDetail {
  id: string;
  candidate_id: string;
  programme_type: string;
  package: string;
  status: string;
  current_phase: string;
  current_session: number;
  candidate_name?: string;
  sessions: SessionDetail[];
  documents: { id: string; document_id: string; stage: number; released_at: string }[];
  micro_actions: { id: string; text: string; status: string; due_date: string; session_id: string }[];
}

export function TrainerClient() {
  usePageTitle('Client Details');
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<EnrolmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [completingSession, setCompletingSession] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [energy, setEnergy] = useState(7);
  const [microTexts, setMicroTexts] = useState(['', '', '']);

  useEffect(() => {
    if (id) {
      api.get<EnrolmentDetail>(`/programmes/${id}`)
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  const handleCompleteSession = async (sessionId: string, sessionNum: number) => {
    try {
      await api.patch(`/programmes/${id}/sessions/${sessionId}`, {
        status: 'complete',
        energy_score: energy,
        trainer_notes: notes,
      });

      // Create micro-actions if provided
      if (microTexts.every((t) => t.trim())) {
        await api.post(`/programmes/${id}/sessions/${sessionId}/micro-actions`, {
          actions: microTexts.map((text) => ({ text })),
        });
      }

      // Refresh data
      const updated = await api.get<EnrolmentDetail>(`/programmes/${id}`);
      setData(updated);
      setCompletingSession(null);
      setNotes('');
      setMicroTexts(['', '', '']);
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  const handleReleaseStage = async (stage: number) => {
    try {
      await api.post('/programmes/documents/release', { enrolment_id: id, stage });
      const updated = await api.get<EnrolmentDetail>(`/programmes/${id}`);
      setData(updated);
    } catch (err) {
      console.error('Release failed:', err);
    }
  };

  if (loading) return <Layout><div className="max-w-5xl mx-auto mt-20 px-6 text-center"><p>Loading...</p></div></Layout>;
  if (!data) return <Layout><div className="max-w-5xl mx-auto mt-20 px-6 text-center"><p className="text-red-600">Not found.</p></div></Layout>;

  const nextSession = data.sessions.find((s) => s.status !== 'complete' && s.status !== 'cancelled');
  const releasedStages = new Set(data.documents.map((d) => d.stage));

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-1">
          Client Details
        </h1>
        <p className="text-mustard-charcoal/60 mb-8">
          {data.programme_type === 'lifespark360' ? 'LifeSpark360' : 'Seedbed'} — {data.package} — Phase {data.current_phase}
        </p>

        {/* Seedbed Discovery Panel — above sessions for Seedbed enrolments */}
        {data.programme_type === 'seedbed' && data.candidate_id && (
          <div className="mb-6">
            <SeedDiscoveryPanel candidateId={data.candidate_id} />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sessions */}
          <div className="lg:col-span-2 space-y-3">
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mb-3">Sessions</h2>
            {data.sessions.map((session) => (
              <div key={session.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${session.status === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      {session.session_num}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-mustard-charcoal">Session {session.session_num} — Phase {session.phase}</p>
                      {session.scheduled_at && <p className="text-xs text-mustard-charcoal/40">{new Date(session.scheduled_at).toLocaleDateString('en-GB')}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${session.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {session.status}
                    </span>
                    {session.status !== 'complete' && session.status !== 'cancelled' && (
                      <button
                        onClick={() => setCompletingSession(session.id)}
                        className="px-3 py-1 bg-mustard-green-dark text-white text-xs rounded font-medium hover:bg-mustard-green transition-colors"
                      >
                        Complete
                      </button>
                    )}
                  </div>
                </div>
                {session.trainer_notes && (
                  <p className="mt-2 text-xs text-mustard-charcoal/50 italic">{session.trainer_notes}</p>
                )}

                {/* Completion form */}
                {completingSession === session.id && (
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-mustard-charcoal">Energy Score (1-10)</label>
                      <input type="number" min={1} max={10} value={energy} onChange={(e) => setEnergy(Number(e.target.value))} className="w-20 ml-2 px-2 py-1 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-mustard-charcoal block mb-1">Session Notes</label>
                      <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-mustard-charcoal block mb-1">Micro-Actions (3 required)</label>
                      {microTexts.map((text, i) => (
                        <input key={i} type="text" value={text} onChange={(e) => { const t = [...microTexts]; t[i] = e.target.value; setMicroTexts(t); }} placeholder={`Micro-action ${i + 1}`} className="w-full px-3 py-2 border rounded text-sm mb-1" />
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleCompleteSession(session.id, session.session_num)} className="px-4 py-2 bg-mustard-green-dark text-white text-sm rounded font-medium">Save & Complete</button>
                      <button onClick={() => setCompletingSession(null)} className="px-4 py-2 border text-sm rounded">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pack Release Controls */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Pack Releases</h3>
              {[1, 2, 3].map((stage) => {
                const config = data.programme_type === 'lifespark360' ? (stage <= 3) : (stage <= 2);
                if (!config) return null;
                const released = releasedStages.has(stage);
                return (
                  <div key={stage} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-mustard-charcoal">Stage {stage}</span>
                    {released ? (
                      <span className="text-xs text-green-600 font-medium">Released</span>
                    ) : (
                      <button onClick={() => handleReleaseStage(stage)} className="text-xs px-3 py-1 bg-mustard-gold text-mustard-green-dark rounded font-medium hover:bg-mustard-gold-light transition-colors">Release</button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Micro-actions overview */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Micro-Actions</h3>
              {data.micro_actions.length === 0 ? (
                <p className="text-xs text-mustard-charcoal/50">No micro-actions yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.micro_actions.slice(0, 9).map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-xs">
                      <span className={`w-2 h-2 rounded-full ${a.status === 'completed' ? 'bg-green-500' : a.status === 'partial' ? 'bg-amber-500' : a.status === 'missed' ? 'bg-red-500' : 'bg-gray-300'}`} />
                      <span className="text-mustard-charcoal truncate">{a.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
